import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { 
  createStoring, 
  updateStoring, 
  fetchStoringById, 
  fetchCategories, 
  fetchExistingAccommodaties,
  fetchExistingKampeerplaatsen 
} from "../Reducers/actions";

const StoringForm = ({ 
  createStoring, 
  updateStoring, 
  fetchStoringById, 
  fetchCategories,
  fetchExistingAccommodaties,
  fetchExistingKampeerplaatsen,
  categories,
  existingAccommodaties,
  existingKampeerplaatsen,
  storingen 
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    titel: '',
    beschrijving: '',
    prioriteit: 'normaal',
    categorie: '',
    accommodatie: '',
    kampeerplaats: '',
    accommodatieType: 'accommodatie',
    urgentie: false,
    fotos: []
  });

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCategories();
    fetchExistingAccommodaties();
    fetchExistingKampeerplaatsen();
    
    if (isEdit && id) {
      fetchStoringById(id).then((storing) => {
        if (storing) {
          setFormData({
            titel: storing.titel || '',
            beschrijving: storing.beschrijving || '',
            prioriteit: storing.prioriteit || 'normaal',
            categorie: storing.categorie?._id || '',
            accommodatie: storing.accommodatie?._id || '',
            kampeerplaats: storing.kampeerplaats?._id || '',
            accommodatieType: storing.accommodatieType || 'accommodatie',
            urgentie: storing.urgentie || false,
            fotos: storing.fotos || []
          });
        }
      });
    }
  }, [isEdit, id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.titel.trim()) {
      newErrors.titel = 'Titel is verplicht';
    }

    if (!formData.beschrijving.trim()) {
      newErrors.beschrijving = 'Beschrijving is verplicht';
    }

    if (!formData.categorie) {
      newErrors.categorie = 'Categorie is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      
      // Add form fields (skip empty values and fotos array)
      Object.keys(formData).forEach(key => {
        if (key !== 'fotos' && formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
          // Skip empty accommodatie/kampeerplaats based on type selection
          if (key === 'accommodatie' && formData.accommodatieType !== 'accommodatie') {
            return;
          }
          if (key === 'kampeerplaats' && formData.accommodatieType !== 'kampeerplaats') {
            return;
          }
          submitData.append(key, formData[key]);
        }
      });

      // Add files
      files.forEach(file => {
        submitData.append('fotos', file);
      });

      let result;
      if (isEdit) {
        result = await updateStoring(id, submitData);
      } else {
        result = await createStoring(submitData);
      }

      if (result) {
        navigate('/facility/storingen');
      }
    } catch (error) {
      console.error('Error submitting storing:', error);
      setErrors({ submit: 'Er is een fout opgetreden bij het opslaan' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/facility/storingen');
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold py-3 mb-2">
            <span className="text-muted fw-light">Facility Management / Storingen /</span>
            {isEdit ? 'Bewerk Storing' : 'Nieuwe Storing'}
          </h4>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-8">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                {isEdit ? 'Storing Bewerken' : 'Nieuwe Storing Aanmaken'}
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Titel */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="titel">
                    Titel <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.titel ? 'is-invalid' : ''}`}
                    id="titel"
                    name="titel"
                    value={formData.titel}
                    onChange={handleInputChange}
                    placeholder="Korte beschrijving van de storing"
                  />
                  {errors.titel && (
                    <div className="invalid-feedback">{errors.titel}</div>
                  )}
                </div>

                {/* Beschrijving */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="beschrijving">
                    Beschrijving <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${errors.beschrijving ? 'is-invalid' : ''}`}
                    id="beschrijving"
                    name="beschrijving"
                    rows="4"
                    value={formData.beschrijving}
                    onChange={handleInputChange}
                    placeholder="Uitgebreide beschrijving van de storing..."
                  ></textarea>
                  {errors.beschrijving && (
                    <div className="invalid-feedback">{errors.beschrijving}</div>
                  )}
                </div>

                <div className="row">
                  {/* Categorie */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="categorie">
                      Categorie <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${errors.categorie ? 'is-invalid' : ''}`}
                      id="categorie"
                      name="categorie"
                      value={formData.categorie}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecteer een categorie</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>
                          {cat.parent ? `${cat.parent.naam} > ${cat.naam}` : cat.naam}
                        </option>
                      ))}
                    </select>
                    {errors.categorie && (
                      <div className="invalid-feedback">{errors.categorie}</div>
                    )}
                  </div>

                  {/* Prioriteit */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="prioriteit">
                      Prioriteit
                    </label>
                    <select
                      className="form-select"
                      id="prioriteit"
                      name="prioriteit"
                      value={formData.prioriteit}
                      onChange={handleInputChange}
                    >
                      <option value="laag">Laag</option>
                      <option value="normaal">Normaal</option>
                      <option value="hoog">Hoog</option>
                      <option value="kritiek">Kritiek</option>
                    </select>
                  </div>
                </div>

                {/* Accommodatie/Kampeerplaats Type Selector */}
                <div className="mb-3">
                  <label className="form-label">
                    Locatie Type
                  </label>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="accommodatie-type"
                          name="accommodatieType"
                          value="accommodatie"
                          checked={formData.accommodatieType === 'accommodatie'}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="accommodatie-type">
                          Accommodatie
                        </label>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="kampeerplaats-type"
                          name="accommodatieType"
                          value="kampeerplaats"
                          checked={formData.accommodatieType === 'kampeerplaats'}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="kampeerplaats-type">
                          Kampeerplaats
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Accommodatie Selector */}
                {formData.accommodatieType === 'accommodatie' && (
                  <div className="mb-3">
                    <label className="form-label" htmlFor="accommodatie">
                      Accommodatie
                    </label>
                    <select
                      className="form-select"
                      id="accommodatie"
                      name="accommodatie"
                      value={formData.accommodatie}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecteer een accommodatie</option>
                      {existingAccommodaties.map(acc => (
                        <option key={acc._id} value={acc._id}>
                          {acc.naam} {acc.beschrijving ? `- ${acc.beschrijving}` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Optioneel: Selecteer de accommodatie waar de storing zich voordoet
                    </div>
                  </div>
                )}

                {/* Kampeerplaats Selector */}
                {formData.accommodatieType === 'kampeerplaats' && (
                  <div className="mb-3">
                    <label className="form-label" htmlFor="kampeerplaats">
                      Kampeerplaats
                    </label>
                    <select
                      className="form-select"
                      id="kampeerplaats"
                      name="kampeerplaats"
                      value={formData.kampeerplaats}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecteer een kampeerplaats</option>
                      {existingKampeerplaatsen.map(kp => (
                        <option key={kp._id} value={kp._id}>
                          {kp.naam} {kp.beschrijving ? `- ${kp.beschrijving}` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Optioneel: Selecteer de kampeerplaats waar de storing zich voordoet
                    </div>
                  </div>
                )}

                {/* Urgentie */}
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="urgentie"
                      name="urgentie"
                      checked={formData.urgentie}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="urgentie">
                      <strong>Urgent</strong> - Deze storing vereist onmiddellijke aandacht
                    </label>
                  </div>
                </div>

                {/* Foto Upload */}
                <div className="mb-3">
                  <label className="form-label" htmlFor="fotos">
                    Foto's toevoegen
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="fotos"
                    name="fotos"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <div className="form-text">
                    Je kunt meerdere foto's selecteren. Ondersteunde formaten: JPG, PNG, GIF
                  </div>
                </div>

                {/* Current Photos (Edit mode) */}
                {isEdit && formData.fotos.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Huidige Foto's</label>
                    <div className="row">
                      {formData.fotos.map((foto, index) => (
                        <div key={index} className="col-md-3 mb-2">
                          <img
                            src={`/api/facility/uploads/${foto.filename}`}
                            alt={foto.originalname}
                            className="img-fluid rounded"
                            style={{ maxHeight: '100px', objectFit: 'cover' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error message */}
                {errors.submit && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bx bx-error-circle me-2"></i>
                    {errors.submit}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-end gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <i className="bx bx-x me-2"></i>
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Laden...</span>
                        </div>
                        {isEdit ? 'Bijwerken...' : 'Aanmaken...'}
                      </>
                    ) : (
                      <>
                        <i className={`bx ${isEdit ? 'bx-save' : 'bx-plus'} me-2`}></i>
                        {isEdit ? 'Bijwerken' : 'Aanmaken'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar with help */}
        <div className="col-xl-4">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="bx bx-help-circle me-2"></i>
                Tips
              </h6>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <h6 className="text-primary">Prioriteit Richtlijnen</h6>
                <ul className="list-unstyled">
                  <li className="mb-1">
                    <span className="badge bg-label-danger me-2">Kritiek</span>
                    Veiligheid, water, elektriciteit
                  </li>
                  <li className="mb-1">
                    <span className="badge bg-label-warning me-2">Hoog</span>
                    Verwarming, airco, belangrijke faciliteiten
                  </li>
                  <li className="mb-1">
                    <span className="badge bg-label-info me-2">Normaal</span>
                    Standaard reparaties
                  </li>
                  <li>
                    <span className="badge bg-label-success me-2">Laag</span>
                    Cosmetische verbeteringen
                  </li>
                </ul>
              </div>

              <div className="mb-3">
                <h6 className="text-primary">Foto Tips</h6>
                <ul className="text-muted">
                  <li>Maak duidelijke foto's van de storing</li>
                  <li>Inclusief overzichtsfoto en detail foto</li>
                  <li>Maximaal 10 foto's per melding</li>
                  <li>Ondersteunde formaten: JPG, PNG, GIF</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  categories: state.facilityManagement?.categories || [],
  existingAccommodaties: state.facilityManagement?.existingAccommodaties || [],
  existingKampeerplaatsen: state.facilityManagement?.existingKampeerplaatsen || [],
  storingen: state.facilityManagement?.storingen || {}
});

const mapDispatchToProps = {
  createStoring,
  updateStoring,
  fetchStoringById,
  fetchCategories,
  fetchExistingAccommodaties,
  fetchExistingKampeerplaatsen
};

export default connect(mapStateToProps, mapDispatchToProps)(StoringForm);